// src/pages/MyPage/index.js
// すべてのUI文言とコメントは日本語で記述しています。
// 機能:
// - タブ: 「練習結果（progress）」「試験結果（testResults）」「仕訳練習結果（journalPractice）」
// - 役割が学生/留学生の場合: 自分のデータのみ表示、Excel出力ボタンは非表示
// - その他の役割: 全学生データを表示、クラス絞り込みとExcel出力が可能
// - Realtime: Firestore onSnapshot を利用して即時反映
// - 試験結果タブ: 試験日（期間）、試験名、ステータス（合格/不合格）で絞り込み＋Excel出力
// - 受験回数（attempts）を学生/管理者の両方に表示、Excelにも含める
// - 仕訳練習結果タブ: journalPracticeResults コレクションを表示

import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Table,
  Tag,
  Button,
  Spin,
  Empty,
  App,
  Radio,
  Space,
  Avatar,
  Select,
  Progress,
  Tabs,
  DatePicker,
} from 'antd';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase-config';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { getExerciseDetailsById } from '../../services/exerciseService';
import { UserOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

/* ================= ヘルパー関数 ================= */

// Firestore Timestamp/Date/number をミリ秒へ
const toMillis = (ts) => {
  if (!ts) return 0;
  if (typeof ts === 'number') return ts;
  if (ts?.toDate) return ts.toDate().getTime();
  if (ts instanceof Date) return ts.getTime();
  return 0;
};

// 時刻表示フォーマット
const fmt = (ts) => (ts ? dayjs(toMillis(ts)).format('YYYY/MM/DD HH:mm') : '-');

// 役割名の正規化（小文字化）
const normalizeRoleName = (roleName) => (roleName || '').toLowerCase();

// 学生/留学生かどうか判定（多言語対応）
const isStudentRole = (roleName) => {
  const r = normalizeRoleName(roleName);
  return (
    r.includes('学生') ||
    r.includes('留学生') ||
    r.includes('học sinh') ||
    r.includes('sinh viên') ||
    r.includes('du học sinh') ||
    r.includes('student')
  );
};

/* ================= コンポーネント ================= */

const MyPage = () => {
  const { currentUser, userRole } = useAuth();
  const { message } = App.useApp();

  // 共通状態
  const [loading, setLoading] = useState(true);
  const roleName = userRole?.name || '';
  const isStudent = isStudentRole(roleName);
  const uid = currentUser?.uid || currentUser?.id;

  // 練習結果（progress）
  const [progressData, setProgressData] = useState([]);

  // 試験結果（testResults）
  const [testResults, setTestResults] = useState([]);

  // 仕訳練習結果（journalPracticeResults）
  const [journalResults, setJournalResults] = useState([]);

  // 学生情報（管理者用）
  const [studentsMap, setStudentsMap] = useState(new Map());
  const [classList, setClassList] = useState([]);

  // フィルタ
  const [statusFilter, setStatusFilter] = useState('all'); // 練習: 状態
  const [classFilter, setClassFilter] = useState('all');   // 管理者: クラス
  const [activeTab, setActiveTab] = useState('practice');  // 'practice' | 'exam' | 'journal'

  // 試験結果のフィルタ
  const [examDateRange, setExamDateRange] = useState([null, null]); // [dayjs|null, dayjs|null]
  const [examTestFilter, setExamTestFilter] = useState('all');      // 試験名
  const [examStatusFilter, setExamStatusFilter] = useState('all');  // 'pass' | 'fail' | 'all'

  /* ---------- Realtime購読設定 ---------- */
  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }
    setLoading(true);

    let unsubPractice = () => {};
    let unsubExam = () => {};
    let unsubJournal = () => {};
    let unsubUsers = () => {};

    try {
      if (isStudent) {
        // 学生/留学生: 自分のprogressのみ
        const qPractice = query(collection(db, 'progress'), where('userId', '==', uid));
        unsubPractice = onSnapshot(
          qPractice,
          (snap) => {
            const arr = snap.docs.map((d) => {
              const p = d.data();
              const details = getExerciseDetailsById(p.exerciseId);
              return {
                ...p,
                id: d.id,
                title: details?.title || p.exerciseId,
              };
            });
            // クライアント側降順
            arr.sort((a, b) => toMillis(b.lastAttempted) - toMillis(a.lastAttempted));
            setProgressData(arr);
          },
          (err) => console.error('progress snapshot error:', err)
        );

        // 学生/留学生: 自分のtestResultsのみ
        const qExam = query(collection(db, 'testResults'), where('userId', '==', uid));
        unsubExam = onSnapshot(
          qExam,
          (snap) => {
            const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            arr.sort((a, b) => toMillis(b.submittedAt) - toMillis(a.submittedAt));
            setTestResults(arr);
          },
          (err) => console.error('testResults snapshot error:', err)
        );

        // 学生/留学生: 自分の仕訳練習結果のみ
        const qJournal = query(
          collection(db, 'journalPracticeResults'),
          where('userId', '==', uid)
        );
        unsubJournal = onSnapshot(
          qJournal,
          (snap) => {
            const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            arr.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
            setJournalResults(arr);
          },
          (err) => console.error('journalPracticeResults snapshot error:', err)
        );
      } else {
        // 管理者/教員など: 全件
        unsubPractice = onSnapshot(
          collection(db, 'progress'),
          (snap) => {
            const arr = snap.docs.map((d) => {
              const p = d.data();
              const details = getExerciseDetailsById(p.exerciseId);
              return {
                ...p,
                id: d.id,
                title: details?.title || p.exerciseId,
              };
            });
            setProgressData(arr);
          },
          (err) => console.error('progress snapshot error:', err)
        );

        unsubExam = onSnapshot(
          collection(db, 'testResults'),
          (snap) => {
            const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setTestResults(arr);
          },
          (err) => console.error('testResults snapshot error:', err)
        );

        // 仕訳練習結果（全件）
        unsubJournal = onSnapshot(
          collection(db, 'journalPracticeResults'),
          (snap) => {
            const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setJournalResults(arr);
          },
          (err) => console.error('journalPracticeResults snapshot error:', err)
        );

        // 学生一覧（クラス表示/紐付け用）
        const qUsers = query(collection(db, 'users'), where('userType', '==', 'student'));
        unsubUsers = onSnapshot(
          qUsers,
          (snap) => {
            const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            const map = new Map(list.map((s) => [s.id, s]));
            setStudentsMap(map);
            const classes = [...new Set(list.map((s) => s.className).filter(Boolean))].sort();
            setClassList(classes);
          },
          (err) => console.error('users snapshot error:', err)
        );
      }
    } catch (err) {
      console.error('Realtime setup error:', err);
      message.error('リアルタイム購読の設定に失敗しました。');
    }

    setLoading(false);

    return () => {
      unsubPractice && unsubPractice();
      unsubExam && unsubExam();
      unsubJournal && unsubJournal();
      unsubUsers && unsubUsers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, isStudent]);

  /* ---------- 派生（学生情報の付与 / テスト名一覧 / 仕訳結果） ---------- */

  const progressWithStudent = useMemo(() => {
    if (isStudent) return progressData;
    return progressData.map((p) => ({
      ...p,
      studentInfo: studentsMap.get(p.userId) || {},
    }));
  }, [progressData, studentsMap, isStudent]);

  const resultsWithStudent = useMemo(() => {
    if (isStudent) return testResults;
    return testResults.map((r) => ({
      ...r,
      studentInfo: studentsMap.get(r.userId) || {},
    }));
  }, [testResults, studentsMap, isStudent]);

  const journalWithStudent = useMemo(() => {
    if (isStudent) return journalResults;
    return journalResults.map((r) => ({
      ...r,
      studentInfo: studentsMap.get(r.userId) || {},
    }));
  }, [journalResults, studentsMap, isStudent]);

  // 試験名のユニーク一覧（試験結果フィルタ用）
  const testNameList = useMemo(() => {
    const names = (resultsWithStudent || []).map((r) => r.testName).filter(Boolean);
    return [...new Set(names)].sort();
  }, [resultsWithStudent]);

  /* ---------- フィルタ適用 ---------- */

  // 練習結果: 状態（完了/未完了）＋（管理者のみ）クラス
  const filteredPractice = useMemo(() => {
    return (progressWithStudent || []).filter((item) => {
      const statusMatch = statusFilter === 'all' || item.status === statusFilter;
      if (isStudent) return statusMatch;
      const classMatch = classFilter === 'all' || item.studentInfo?.className === classFilter;
      return statusMatch && classMatch;
    });
  }, [progressWithStudent, statusFilter, classFilter, isStudent]);

  // 試験結果: 試験日（期間）/ 試験名 / ステータス（合格/不合格）＋（管理者のみ）クラス
  const filteredExam = useMemo(() => {
    const [d1, d2] = examDateRange || [];
    const startMs = d1 ? d1.startOf('day').valueOf() : null;
    const endMs = d2 ? d2.endOf('day').valueOf() : null;

    return (resultsWithStudent || []).filter((item) => {
      // クラス（管理者のみ）
      if (!isStudent) {
        const classMatch = classFilter === 'all' || item?.studentInfo?.className === classFilter;
        if (!classMatch) return false;
      }
      // 試験日
      const t = toMillis(item.submittedAt);
      if (startMs && t < startMs) return false;
      if (endMs && t > endMs) return false;
      // 試験名
      if (examTestFilter !== 'all' && item.testName !== examTestFilter) return false;
      // ステータス（合格/不合格）
      if (examStatusFilter !== 'all') {
        const total = Number(item.totalScore || 0);
        const pass = Number(item.score || 0) >= total / 2;
        if (examStatusFilter === 'pass' && !pass) return false;
        if (examStatusFilter === 'fail' && pass) return false;
      }
      return true;
    });
  }, [resultsWithStudent, examDateRange, examTestFilter, examStatusFilter, classFilter, isStudent]);

  // 仕訳練習結果: （管理者のみ）クラスで絞り込み
  const filteredJournal = useMemo(() => {
    return (journalWithStudent || []).filter((item) => {
      if (isStudent) return true;
      const classMatch = classFilter === 'all' || item?.studentInfo?.className === classFilter;
      return classMatch;
    });
  }, [journalWithStudent, classFilter, isStudent]);

  /* ---------- テーブル列定義（練習結果） ---------- */

  // 学生用（練習）
  const studentPracticeCols = [
    { title: '課題', dataIndex: 'title', key: 'title' },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status) => (
        <Tag color={status === 'completed' ? 'green' : 'volcano'}>
          {status === 'completed' ? '完了' : '未完了'}
        </Tag>
      ),
    },
    { title: '正解数', dataIndex: 'completedCount', key: 'completedCount', align: 'center' },
    { title: '挑戦回数', dataIndex: 'attempts', key: 'attempts', align: 'center' },
    {
      title: '正解率',
      key: 'successRate',
      align: 'center',
      render: (_, record) => {
        const { completedCount = 0, attempts = 0 } = record;
        const rate = attempts ? Math.round((completedCount / attempts) * 100) : 0;
        return <Progress percent={rate} size="small" />;
      },
    },
    {
      title: '操作',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Link to={`/level/${record.levelId}/chapter/${record.chapterId}/exercise/${record.exerciseId}`}>
          <Button type="primary">再挑戦</Button>
        </Link>
      ),
    },
  ];

  // 管理者用（練習）
  const adminPracticeCols = [
    {
      title: '画像',
      dataIndex: ['studentInfo', 'avatar'],
      key: 'avatar',
      render: (url) => <Avatar src={url} icon={<UserOutlined />} />,
    },
    { title: '学生ID', dataIndex: ['studentInfo', 'studentId'], key: 'studentId' },
    { title: '氏名（ローマ字）', dataIndex: ['studentInfo', 'nameRomaji'], key: 'nameRomaji' },
    { title: 'クラス', dataIndex: ['studentInfo', 'className'], key: 'className' },
    { title: '出席番号', dataIndex: ['studentInfo', 'regNumber'], key: 'regNumber' },
    { title: '課題', dataIndex: 'title', key: 'title' },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status) => (
        <Tag color={status === 'completed' ? 'green' : 'volcano'}>
          {status === 'completed' ? '完了' : '未完了'}
        </Tag>
      ),
    },
    { title: '正解数', dataIndex: 'completedCount', key: 'completedCount', align: 'center' },
    { title: '挑戦回数', dataIndex: 'attempts', key: 'attempts', align: 'center' },
    {
      title: '正解率',
      key: 'successRate',
      align: 'center',
      render: (_, record) => {
        const { completedCount = 0, attempts = 0 } = record;
        const rate = attempts ? (completedCount / attempts) * 100 : 0;
        return `${rate.toFixed(1)}%`;
      },
    },
  ];

  /* ---------- テーブル列定義（試験結果） ---------- */
  // 学生用（試験）
  const studentExamCols = [
    { title: '試験名', dataIndex: 'testName', key: 'testName' },
    {
      title: '得点 / 配点',
      key: 'score',
      align: 'center',
      render: (_, r) => `${Number(r.score || 0)} / ${Number(r.totalScore || 0)}`,
    },
    {
      title: '正答率',
      key: 'pct',
      align: 'center',
      render: (_, r) => {
        const total = Number(r.totalScore || 0);
        const pct = total ? Math.round((Number(r.score || 0) / total) * 100) : 0;
        return <Progress percent={pct} size="small" />;
      },
    },
    {
      title: '合否',
      key: 'pass',
      align: 'center',
      render: (_, r) => {
        const total = Number(r.totalScore || 0);
        const pass = Number(r.score || 0) >= total / 2;
        return <Tag color={pass ? 'green' : 'volcano'}>{pass ? '合格' : '不合格'}</Tag>;
      },
    },
    { title: '受験回数', dataIndex: 'attempts', key: 'attempts', align: 'center', render: (v) => Number(v || 0) },
    { title: '提出時刻', dataIndex: 'submittedAt', key: 'submittedAt', align: 'center', render: fmt },
  ];

  // 管理者用（試験）
  const adminExamCols = [
    {
      title: '画像',
      dataIndex: ['studentInfo', 'avatar'],
      key: 'avatar',
      render: (url) => <Avatar src={url} icon={<UserOutlined />} />,
    },
    { title: '学生ID', dataIndex: ['studentInfo', 'studentId'], key: 'studentId' },
    { title: '氏名（ローマ字）', dataIndex: ['studentInfo', 'nameRomaji'], key: 'nameRomaji' },
    { title: 'クラス', dataIndex: ['studentInfo', 'className'], key: 'className' },
    { title: '出席番号', dataIndex: ['studentInfo', 'regNumber'], key: 'regNumber' },
    { title: '試験名', dataIndex: 'testName', key: 'testName' },
    {
      title: '得点 / 配点',
      key: 'score',
      align: 'center',
      render: (_, r) => `${Number(r.score || 0)} / ${Number(r.totalScore || 0)}`,
    },
    {
      title: '正答率',
      key: 'pct',
      align: 'center',
      render: (_, r) => {
        const total = Number(r.totalScore || 0);
        const pct = total ? Math.round((Number(r.score || 0) / total) * 100) : 0;
        return <Progress percent={pct} size="small" />;
      },
    },
    {
      title: '合否',
      key: 'pass',
      align: 'center',
      render: (_, r) => {
        const total = Number(r.totalScore || 0);
        const pass = Number(r.score || 0) >= total / 2;
        return <Tag color={pass ? 'green' : 'volcano'}>{pass ? '合格' : '不合格'}</Tag>;
      },
    },
    { title: '受験回数', dataIndex: 'attempts', key: 'attempts', align: 'center', render: (v) => Number(v || 0) },
    { title: '提出時刻', dataIndex: 'submittedAt', key: 'submittedAt', align: 'center', render: fmt },
  ];

  /* ---------- テーブル列定義（仕訳練習結果） ---------- */

  // 学生用（仕訳練習結果）
  const studentJournalCols = [
    { title: '実施日時', dataIndex: 'createdAt', key: 'createdAt', align: 'center', render: fmt },
    { title: '総問題数', dataIndex: 'totalQuestions', key: 'totalQuestions', align: 'center' },
    { title: '正解数', dataIndex: 'correctAnswers', key: 'correctAnswers', align: 'center' },
    {
      title: '誤答数',
      key: 'wrongCount',
      align: 'center',
      render: (_, r) =>
        Number(r.totalQuestions || 0) - Number(r.correctAnswers || 0),
    },
    {
      title: '正答率',
      key: 'accuracy',
      align: 'center',
      render: (_, r) => (
        <Progress percent={Number(r.accuracy || 0)} size="small" />
      ),
    },
  ];

  // 管理者用（仕訳練習結果）
  const adminJournalCols = [
    {
      title: '画像',
      dataIndex: ['studentInfo', 'avatar'],
      key: 'avatar',
      render: (url) => <Avatar src={url} icon={<UserOutlined />} />,
    },
    { title: '学生ID', dataIndex: ['studentInfo', 'studentId'], key: 'studentId' },
    { title: '氏名（ローマ字）', dataIndex: ['studentInfo', 'nameRomaji'], key: 'nameRomaji' },
    { title: 'クラス', dataIndex: ['studentInfo', 'className'], key: 'className' },
    { title: '出席番号', dataIndex: ['studentInfo', 'regNumber'], key: 'regNumber' },
    { title: '実施日時', dataIndex: 'createdAt', key: 'createdAt', align: 'center', render: fmt },
    { title: '総問題数', dataIndex: 'totalQuestions', key: 'totalQuestions', align: 'center' },
    { title: '正解数', dataIndex: 'correctAnswers', key: 'correctAnswers', align: 'center' },
    {
      title: '誤答数',
      key: 'wrongCount',
      align: 'center',
      render: (_, r) =>
        Number(r.totalQuestions || 0) - Number(r.correctAnswers || 0),
    },
    {
      title: '正答率',
      key: 'accuracy',
      align: 'center',
      render: (_, r) => `${Number(r.accuracy || 0).toFixed(1)}%`,
    },
  ];

  /* ---------- Excel出力（管理者のみボタン表示） ---------- */

  const writeXlsx = (rows, sheetName, fileName) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, fileName);
  };

  // 練習結果をエクスポート
  const exportPracticeExcel = () => {
    const rows = filteredPractice.map((r) => {
      const base = {
        課題ID: r.exerciseId || '',
        課題名: r.title || '',
        ステータス: r.status === 'completed' ? '完了' : '未完了',
        正解数: Number(r.completedCount || 0),
        挑戦回数: Number(r.attempts || 0),
        最終挑戦: fmt(r.lastAttempted),
      };
      if (!isStudent) {
        return {
          学生ID: r.studentInfo?.studentId || '',
          氏名ローマ字: r.studentInfo?.nameRomaji || '',
          クラス: r.studentInfo?.className || '',
          出席番号: r.studentInfo?.regNumber || '',
          ...base,
        };
      }
      return base;
    });

    writeXlsx(rows, 'PracticeResults', `練習結果_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
  };

  // 試験結果をエクスポート
  const exportExamExcel = () => {
    const rows = filteredExam.map((r) => {
      const total = Number(r.totalScore || 0);
      const score = Number(r.score || 0);
      const pass = score >= total / 2;

      const base = {
        試験名: r.testName || '',
        得点: score,
        配点: total,
        正答率: total ? Math.round((score / total) * 100) : 0,
        合否: pass ? '合格' : '不合格',
        受験回数: Number(r.attempts || 0),
        提出時刻: fmt(r.submittedAt),
      };

      if (!isStudent) {
        return {
          学生ID: r.studentInfo?.studentId || '',
          氏名ローマ字: r.studentInfo?.nameRomaji || '',
          クラス: r.studentInfo?.className || '',
          出席番号: r.studentInfo?.regNumber || '',
          ...base,
        };
      }
      return base;
    });

    writeXlsx(rows, 'ExamResults', `試験結果_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
  };

  // 仕訳練習結果をエクスポート
  const exportJournalExcel = () => {
    const rows = filteredJournal.map((r) => {
      const total = Number(r.totalQuestions || 0);
      const correct = Number(r.correctAnswers || 0);
      const wrong = total - correct;
      const base = {
        実施日時: fmt(r.createdAt),
        総問題数: total,
        正解数: correct,
        誤答数: wrong,
        正答率: Number(r.accuracy || 0),
      };
      if (!isStudent) {
        return {
          学生ID: r.studentInfo?.studentId || '',
          氏名ローマ字: r.studentInfo?.nameRomaji || '',
          クラス: r.studentInfo?.className || '',
          出席番号: r.studentInfo?.regNumber || '',
          ...base,
        };
      }
      return base;
    });

    writeXlsx(
      rows,
      'JournalPracticeResults',
      `仕訳練習結果_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`
    );
  };

  /* ================= UI ================= */

  if (loading) {
    return <Spin size="large" style={{ display: 'block', marginTop: 50 }} />;
  }

  return (
    <div>
      <Title level={2}>{isStudent ? 'マイページ' : '全学生の進捗・結果管理'}</Title>

      {/* 管理者のみ: クラス絞り込み */}
      {!isStudent && (
        <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
          <Text>クラスで絞り込み:</Text>
          <Select
            value={classFilter}
            onChange={setClassFilter}
            style={{ width: 200 }}
            placeholder="すべてのクラス"
            allowClear={false}
          >
            <Option value="all">すべてのクラス</Option>
            {classList.map((className) => (
              <Option key={className} value={className}>
                {className}
              </Option>
            ))}
          </Select>
        </Space>
      )}

      {/* 練習・状態フィルタ（Excelボタンは学生には非表示） */}
      <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <Text>状態（練習）:</Text>
        <Radio.Group value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <Radio.Button value="all">すべて</Radio.Button>
          <Radio.Button value="completed">完了</Radio.Button>
          <Radio.Button value="incomplete">未完了</Radio.Button>
        </Radio.Group>

        {!isStudent && (
          <Button icon={<DownloadOutlined />} onClick={exportPracticeExcel}>
            Excel にエクスポート（練習）
          </Button>
        )}
      </Space>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'practice',
            label: '練習結果',
            children: (
              <Table
                columns={isStudent ? studentPracticeCols : adminPracticeCols}
                dataSource={filteredPractice.map((p) => ({ ...p, key: p.id }))}
                bordered
                locale={{ emptyText: <Empty description="データがありません。" /> }}
              />
            ),
          },
          {
            key: 'exam',
            label: '試験結果',
            children: (
              <>
                {/* 試験結果フィルタ */}
                <Space style={{ marginBottom: 16, flexWrap: 'wrap' }} align="center">
                  <Text>試験日:</Text>
                  <RangePicker
                    value={examDateRange}
                    onChange={setExamDateRange}
                    allowEmpty={[true, true]}
                    format="YYYY/MM/DD"
                  />
                  <Text style={{ marginLeft: 8 }}>試験:</Text>
                  <Select
                    value={examTestFilter}
                    onChange={setExamTestFilter}
                    style={{ width: 220 }}
                  >
                    <Option value="all">すべて</Option>
                    {testNameList.map((name) => (
                      <Option key={name} value={name}>
                        {name}
                      </Option>
                    ))}
                  </Select>
                  <Text style={{ marginLeft: 8 }}>ステータス:</Text>
                  <Select
                    value={examStatusFilter}
                    onChange={setExamStatusFilter}
                    style={{ width: 160 }}
                  >
                    <Option value="all">すべて</Option>
                    <Option value="pass">合格</Option>
                    <Option value="fail">不合格</Option>
                  </Select>

                  {!isStudent && (
                    <Button
                      icon={<DownloadOutlined />}
                      type="primary"
                      onClick={exportExamExcel}
                    >
                      Excel にエクスポート（試験結果）
                    </Button>
                  )}
                </Space>

                <Table
                  columns={isStudent ? studentExamCols : adminExamCols}
                  dataSource={filteredExam.map((r) => ({ ...r, key: r.id }))}
                  bordered
                  locale={{ emptyText: <Empty description="データがありません。" /> }}
                />
              </>
            ),
          },
          {
            key: 'journal',
            label: '仕訳練習結果',
            children: (
              <>
                {!isStudent && (
                  <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                    <Button
                      icon={<DownloadOutlined />}
                      type="primary"
                      onClick={exportJournalExcel}
                    >
                      Excel にエクスポート（仕訳練習）
                    </Button>
                  </Space>
                )}

                <Table
                  columns={isStudent ? studentJournalCols : adminJournalCols}
                  dataSource={filteredJournal.map((r) => ({ ...r, key: r.id }))}
                  bordered
                  locale={{ emptyText: <Empty description="データがありません。" /> }}
                />
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default MyPage;
